// Pure onboarding-journey logic — no React, no side effects. Extracted so the
// ReviewStructure page stays presentational and this state-shaping is unit-testable.
//
// The confirm step posts to Operator::OnboardingController#confirm_and_compute which
// runs UpdateRoomsSetupMutation — the SAME contract the Pricing Rules Rooms Setup page
// uses. Nothing here may change that payload shape.

import type { RoomsSetupRoomType } from '@/types/shared'

// ─── The four stations of the journey ────────────────────────────────────────
// One shared definition so Index (steps 1–2), ReviewStructure (step 3) and Finish
// (step 4) can never drift on labels. Copy is operator-facing — no API jargon.
export interface OnboardingStepDef {
  key: 'connect' | 'choose' | 'review' | 'finish'
  label: string
  description: string
}

export const ONBOARDING_STEPS: OnboardingStepDef[] = [
  { key: 'connect', label: 'Connect', description: 'Link your PMS account' },
  { key: 'choose', label: 'Choose property', description: 'Pick the hotel to add' },
  { key: 'review', label: 'Review', description: 'Check what we found' },
  { key: 'finish', label: 'Finish', description: 'Hand off to the calendar' },
]

// Passing steps.length + 1 as currentStep renders every station completed —
// the Finish page uses this once the journey is done.
export const ONBOARDING_COMPLETE_STEP = ONBOARDING_STEPS.length + 1

// ─── Review-step state builders (mirror RoomsSetup.tsx) ──────────────────────

export interface BoundsState {
  min: string
  max: string
}

export interface DerivedPlanInput {
  offset_type: 'percentage' | 'absolute'
  offset_value: string
}

export interface LeadPlanState {
  leadRatePlanId: number | null
  derivedPlans: Record<string, DerivedPlanInput>
}

export type RatesState = Record<string, Record<string, string>>

/**
 * Seed the editable rate inputs from what the structure sync found in Eviivo.
 * Every room gets a key per occupancy 1..maxOccupancy; occupancies with no
 * seeded value become '' (rendered as the "no rate found in your PMS" invite).
 */
export function buildRatesState(
  roomTypes: RoomsSetupRoomType[],
  baseRates: Record<string, Record<string, number>>
): RatesState {
  const result: RatesState = {}
  for (const rt of roomTypes) {
    result[rt.eviivoRoomTypeId] = {}
    for (let occ = 1; occ <= rt.maxOccupancy; occ++) {
      const key = String(occ)
      const value = baseRates[rt.eviivoRoomTypeId]?.[key]
      result[rt.eviivoRoomTypeId][key] = value != null ? String(value) : ''
    }
  }
  return result
}

export function buildBoundsState(roomTypes: RoomsSetupRoomType[]): Record<string, BoundsState> {
  const result: Record<string, BoundsState> = {}
  for (const rt of roomTypes) {
    result[String(rt.id)] = {
      min: rt.minRate != null ? String(rt.minRate) : '',
      max: rt.maxRate != null ? String(rt.maxRate) : '',
    }
  }
  return result
}

/**
 * Initial lead-plan answers: the currently-flagged lead plan per room, plus any
 * already-stored derived-plan offsets for the non-lead plans. Per the global
 * offset rule, an explicitly-stored 'absolute' is preserved; anything else
 * (null/unset/'percentage') defaults to 'percentage'.
 */
export function buildLeadPlansState(
  roomTypes: RoomsSetupRoomType[]
): Record<string, LeadPlanState> {
  const result: Record<string, LeadPlanState> = {}
  for (const rt of roomTypes) {
    const derived: Record<string, DerivedPlanInput> = {}
    for (const rp of rt.ratePlans || []) {
      if (rp.id !== rt.leadRatePlanId && rp.offsetType && rp.offsetValue != null) {
        derived[String(rp.id)] = {
          offset_type: rp.offsetType === 'absolute' ? 'absolute' : 'percentage',
          offset_value: String(rp.offsetValue),
        }
      }
    }
    result[String(rt.id)] = {
      leadRatePlanId: rt.leadRatePlanId,
      derivedPlans: derived,
    }
  }
  return result
}

export interface LeadPlansPayload {
  [roomTypeId: string]: {
    lead_rate_plan_id: number
    derived_plans: Record<string, DerivedPlanInput>
  }
}

/**
 * The lead_plans piece of the confirm payload. Rooms whose lead plan is still
 * unanswered (null) are OMITTED — skipping is a first-class outcome, and the
 * backend must not receive a fake answer for a question nobody answered.
 */
export function buildLeadPlansPayload(
  leadPlans: Record<string, LeadPlanState>
): LeadPlansPayload {
  const payload: LeadPlansPayload = {}
  for (const [rtId, state] of Object.entries(leadPlans)) {
    if (state.leadRatePlanId != null) {
      payload[rtId] = {
        lead_rate_plan_id: state.leadRatePlanId,
        derived_plans: state.derivedPlans,
      }
    }
  }
  return payload
}

// ─── Display helpers (string inspection only — never rate arithmetic) ────────

/** True when at least one occupancy for this room has a usable staged rate. */
export function roomHasRate(rt: RoomsSetupRoomType, rates: RatesState): boolean {
  const rtRates = rates[rt.eviivoRoomTypeId]
  if (!rtRates) return false
  for (let occ = 1; occ <= rt.maxOccupancy; occ++) {
    const val = rtRates[String(occ)]
    if (val && val.trim() !== '' && Number(val) > 0) return true
  }
  return false
}

/** How many rooms currently have at least one staged rate (for the summary strip). */
export function countRoomsWithRates(
  roomTypes: RoomsSetupRoomType[],
  rates: RatesState
): number {
  return roomTypes.filter((rt) => roomHasRate(rt, rates)).length
}

/**
 * The staged rate values for a room in display order, verbatim (no rounding,
 * no formatting math — these are the operator's own input strings).
 * Per-room rooms read occupancy 1 only; per-occupancy rooms read 1..maxOccupancy.
 */
export function stagedRateEntries(
  rt: RoomsSetupRoomType,
  rates: RatesState
): { occupancy: number; value: string }[] {
  const rtRates = rates[rt.eviivoRoomTypeId] || {}
  const occCount = rt.priceStrategy === 'per_room' ? 1 : rt.maxOccupancy
  const entries: { occupancy: number; value: string }[] = []
  for (let occ = 1; occ <= occCount; occ++) {
    const val = rtRates[String(occ)]
    if (val && val.trim() !== '') entries.push({ occupancy: occ, value: val })
  }
  return entries
}
